// Eval Suite — the console the team reads to judge whether the product is
// getting better.
//
//   EvalOpsView   /chatbot/eval-ops    the offline eval console
//   ModelLabView  /chatbot/model-lab   re-exported so the shell keeps one import
//
// One tab per file under ./; this module is the shell: run picker, headline
// cards and the tab strip. The rules the whole screen is built around live in
// ./format.tsx and are enforced by the formatters there.

import { useCallback, useEffect, useMemo, useState } from "react";

import { http, qs } from "../api";
import { useSession } from "../App";
import { Badge, Banner, SkeletonRows, Spinner, Tabs, Toggle, useAsync } from "../ui";
import {
  ErrorBanner,
  MetricCard,
  dash,
  fmtDuration,
  fmtInt,
  fmtMs,
  fmtNum3,
  fmtPct,
  fmtTime,
  num,
  toEpoch,
} from "./format";
import type { EvalRun, RunsResponse, SuiteDetail } from "./types";

import { AnalysisTab } from "./analysis";
import { CacheTab } from "./cache";
import { CalibrationTab } from "./calibration";
import { FailuresTab } from "./failures";
import { JourneyTab } from "./journey";
import { LanesTab } from "./lanes";
import { MatrixTab } from "./matrix";
import { ReviewTab } from "./review";
import { ThreadsTab } from "./threads";
import { TrendTab } from "./trend";

export { ModelLabView } from "./modellab";

// ============================================================ EvalOpsView

// The chips filter the RUN LIST. They deliberately do not filter turns inside a
// run: a run is one measurement taken at one moment, and slicing its turns by
// wall-clock would produce a pass rate for half an experiment.
const RANGES = [
  { key: "1h", label: "1h", hours: 1 },
  { key: "24h", label: "24h", hours: 24 },
  { key: "7d", label: "7d", hours: 168 },
  { key: "30d", label: "30d", hours: 720 },
];

export function EvalOpsView() {
  const { can } = useSession();
  const canWrite = can("settings.chatbot", "write") || can("chatbot.ai", "write");

  const [range, setRange] = useState("7d");
  const [runId, setRunId] = useState("");
  const [tab, setTab] = useState("matrix");
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [nonce, setNonce] = useState(0);

  const [rowAxis, setRowAxis] = useState("scam_strata");
  const [rowAxis2, setRowAxis2] = useState("user_strata");
  const [colAxis, setColAxis] = useState("lang");
  const [family, setFamily] = useState("");
  const [filters, setFilters] = useState<Record<string, string>>({});
  const filterKey = JSON.stringify(filters);

  const runs = useAsync<RunsResponse>(() => http.get<RunsResponse>(`/chatbot/eval-ops/runs${qs({ limit: 100 })}`), [nonce]);
  const allRuns = useMemo(() => runs.data?.runs ?? [], [runs.data]);

  const rangeHours = RANGES.find((r) => r.key === range)?.hours ?? 168;
  const runTime = (run: EvalRun) => toEpoch(run.recorded_at ?? run.started_at);
  const rangeCount = (hours: number) =>
    allRuns.filter((run) => {
      const t = runTime(run);
      return t > 0 && Date.now() - t <= hours * 3600_000;
    }).length;

  // NEWEST FIRST, and the default is the newest run — not the biggest. Sorting by
  // turn count once made the screen open on whatever the largest run in the window
  // was, routinely a week old and predating half the instruments, so every card
  // read "not instrumented" and the whole screen looked broken on load.
  const runOptions = useMemo(
    () =>
      allRuns
        .filter((run) => {
          const t = runTime(run);
          return t > 0 && Date.now() - t <= rangeHours * 3600_000;
        })
        .sort((a, b) => runTime(b) - runTime(a)),
    [allRuns, rangeHours]
  );

  useEffect(() => {
    if (!allRuns.length) return;
    if (runId && allRuns.some((r) => r.run_id === runId)) return;
    // Newest in the window; failing that, newest overall — the window can be empty
    // on a box that has not run an eval today, and an empty picker is worse than
    // an out-of-window run.
    const newest = [...allRuns].sort((a, b) => runTime(b) - runTime(a))[0];
    setRunId(String((runOptions[0] ?? newest)?.run_id ?? ""));
  }, [allRuns, runOptions, runId]);

  // A run leaving the window snaps the selection to the newest one still in it.
  useEffect(() => {
    if (!runId || !runOptions.length) return;
    if (!runOptions.some((r) => r.run_id === runId)) setRunId(runOptions[0].run_id);
    // Only when the window itself moves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const selectRun = useCallback((id: string) => {
    setRunId(id);
    setFamily("");
    setFilters({});
  }, []);

  const detail = useAsync<SuiteDetail>(
    () =>
      runId
        ? http.get<SuiteDetail>(
            `/chatbot/eval-ops/suite${qs({
              run_id: runId,
              row_axis: rowAxis,
              row_axis_2: rowAxis2,
              col_axis: colAxis,
              family,
              ...filters,
            })}`
          )
        : Promise.resolve({}),
    [runId, rowAxis, rowAxis2, colAxis, family, filterKey, nonce]
  );

  useEffect(() => {
    if (!autoRefresh) return;
    const t = window.setInterval(() => setNonce((n) => n + 1), 30_000);
    return () => window.clearInterval(t);
  }, [autoRefresh]);

  const setFilter = (key: string, value: string) =>
    setFilters((f) => {
      const next = { ...f };
      if (String(value ?? "").trim() === "") delete next[key];
      else next[key] = value;
      return next;
    });

  const data = detail.data;
  const selectedRun = allRuns.find((r) => r.run_id === runId) ?? null;
  // Annotated because EvalRun has a required run_id: without it the `?? {}`
  // fallback widens to a union and every field read below fails to narrow.
  const run: Partial<EvalRun> = data?.run ?? {};
  const judge = data?.judge ?? {};
  const tokens = data?.tokens ?? {};
  const latency = data?.latency ?? {};
  const persona = data?.persona ?? {};
  const cache = data?.cache ?? {};
  const necessity = data?.necessity ?? {};

  // `instrumented: false` suppresses both necessity numbers: an unmeasured run
  // would otherwise report the two most flattering figures on the screen (100%
  // zero-LLM, 0% avoidable) purely because nothing looked.
  const necessityNote = necessity.instrumented
    ? null
    : necessity.note ?? "LLM necessity is not instrumented for this run";

  const headlineTone =
    data?.pass_rate === null || data?.pass_rate === undefined
      ? "var(--text-3)"
      : Number(data.pass_rate) >= 0.99
        ? "var(--success)"
        : Number(data.pass_rate) >= 0.9
          ? "var(--ink)"
          : Number(data.pass_rate) >= 0.75
            ? "var(--warning)"
            : "var(--critical)";

  // Every element measured, none assumed.
  const provenance = useMemo(() => {
    const parts: string[] = [];
    parts.push(dash(run.gen_model) === "—" ? "gen model not recorded" : String(run.gen_model));
    const graders = data?.axis_breakdown?.grader ?? [];
    parts.push(
      dash(run.judge_model) !== "—"
        ? `judge ${run.judge_model}`
        : graders.length
          ? `graders ${graders.map((g) => g.value || "(blank)").join("+")}`
          : "judge not recorded"
    );
    parts.push(tokens.instrumented ? `${fmtInt(tokens.measured_total)} tok` : "tokens not instrumented");
    parts.push(fmtDuration(run.duration_s));
    if (run.git_sha) parts.push(`@${run.git_sha}`);
    if (run.harness) parts.push(String(run.harness));
    return parts.join(" · ");
  }, [run, data, tokens]);

  // `backendUp` is derived from a real call, not assumed: the dot is green only
  // after the runs endpoint has answered at least once.
  const backendUp = runs.loading && !runs.data ? null : runs.error ? false : true;

  const hasTurns = num(data?.total) > 0;
  const busy = runs.loading || detail.loading;

  // Calibration and Journeys sit high on purpose. Calibration says whether the
  // judge behind half the numbers on this screen is an instrument at all, and
  // Journeys is the shape the product actually ships in. Both belong in front of
  // the reader, not behind six tabs of aggregate.
  const tabs = [
    { id: "matrix", label: "Matrix" },
    { id: "calibration", label: "Judge calibration" },
    { id: "journey", label: "Journeys" },
    { id: "analysis", label: "Analysis" },
    { id: "trend", label: "Trend" },
    { id: "threads", label: "Threads" },
    { id: "lanes", label: "Lanes" },
    { id: "cache", label: "Cache" },
    { id: "review", label: `Review (${fmtInt(data?.review_total)})` },
    { id: "failures", label: `Failures (${fmtInt(data?.failures_total)})` },
  ];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Eval Suite</h1>
          <div className="sub">
            Offline eval suite — segmented pass rate, lanes, latency and the failures behind the headline. No
            aggregate is printed without the n it came from, and a missing instrument is never rendered as a number.
          </div>
        </div>
        <div className="page-actions">
          <div className="btn-group" role="group" aria-label="Run window">
            {RANGES.map((option) => (
              <button
                key={option.key}
                className={`btn sm${range === option.key ? " active" : ""}`}
                title={`${rangeCount(option.hours)} runs in the last ${option.label}`}
                onClick={() => setRange(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <Badge tone={backendUp === false ? "critical" : backendUp ? "good" : "neutral"} title={runs.error?.message}>
            {backendUp === false ? "backend down" : backendUp ? "backend up" : "checking"}
          </Badge>
          <button className="btn" onClick={() => setNonce((n) => n + 1)} disabled={busy}>
            {busy && <span className="spinner" />}
            Refresh
          </button>
        </div>
      </div>

      <ErrorBanner error={runs.error} what="Failed to load eval runs" />

      <section className="card">
        <div className="row" style={{ alignItems: "flex-start", flexWrap: "wrap", gap: "var(--sp-5)" }}>
          <div>
            <div className="row" style={{ alignItems: "baseline" }}>
              <span style={{ fontSize: 44, fontWeight: 600, lineHeight: 1, color: headlineTone }}>
                {fmtPct(data?.pass_rate, 1)}
              </span>
              <span className="muted">
                turn pass
                {hasTurns ? `, ${fmtInt(data?.passed)}/${fmtInt(data?.total)} turns` : ""}
              </span>
            </div>
            <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              {provenance}
            </p>
          </div>

          <div className="spacer" />

          <div style={{ minWidth: 340, flex: 1, maxWidth: 640 }}>
            <select
              value={runId}
              onChange={(e) => selectRun(e.target.value)}
              disabled={runs.loading}
              aria-label="Eval run"
            >
              {!runOptions.length && <option value="">No runs recorded in the last {range}</option>}
              {runOptions.map((option) => (
                <option key={option.run_id} value={option.run_id}>
                  {option.run_id} · {dash(option.suite)} ·{" "}
                  {/* total_turns is written when the run FINISHES, so a run still in
                      flight is labelled by its status rather than accused of having
                      no rows. */}
                  {num(option.total_turns)
                    ? `${fmtInt(option.total_turns)} turns`
                    : option.status && option.status !== "done"
                      ? option.status
                      : "no per-turn rows"}{" "}
                  · {fmtTime(option.recorded_at ?? option.started_at)}
                </option>
              ))}
            </select>
            <div className="row muted" style={{ fontSize: 11, marginTop: 6, flexWrap: "wrap" }}>
              <span>{fmtInt(data?.cases ?? selectedRun?.total)} cases</span>
              <span>{fmtInt(data?.conversations)} threads</span>
              <span>{fmtTime(selectedRun?.recorded_at ?? selectedRun?.started_at)}</span>
              <span>
                F1 <span className="strong num">{fmtNum3(judge.micro_f1)}</span>
              </span>
              {selectedRun?.status && selectedRun.status !== "done" && <Badge tone="info">{selectedRun.status}</Badge>}
              {detail.loading && <Spinner label="Loading run detail" />}
            </div>
          </div>
        </div>

        <ErrorBanner error={detail.error} what="Failed to load the run" />
        {!detail.error && data?.empty_reason && <Banner tone="warning">{data.empty_reason}</Banner>}

        <div className="grid c4" style={{ marginTop: "var(--sp-4)" }}>
          <MetricCard
            label="Cases"
            value={fmtInt(data?.cases)}
            sub={`${fmtPct(data?.served_ok_share, 0)} served-ok`}
            sub2={`${fmtInt(data?.total)} turns · ${fmtInt(data?.conversations)} threads`}
            hint="A turn is served-ok when the run recorded a non-empty reply for it."
          />
          <MetricCard
            label="Judge / gate"
            value={fmtPct(judge.agreement)}
            sub={`${fmtInt(judge.agree)}/${fmtInt(judge.judged_n)} judged`}
            sub2={`${fmtInt(judge.false_pass)} hidden breakage`}
            tone={num(judge.false_pass) > 0 ? "bad" : "default"}
            note={
              num(judge.judged_n)
                ? null
                : "no turn in this run carries a judge verdict — every number here is the deterministic gate alone"
            }
            hint="Agreement between the deterministic gate and the LLM judge on the same turn."
          />
          <MetricCard
            label="Macro-F1"
            value={fmtNum3(judge.macro_f1)}
            sub={`weighted ${fmtNum3(judge.weighted_f1)} · micro ${fmtNum3(judge.micro_f1)}`}
            sub2="gate vs judge, not detection"
            note={
              judge.macro_f1 === null || judge.macro_f1 === undefined
                ? "nothing judged — an F1 needs a reference label and this run has none"
                : null
            }
            hint={judge.definition}
          />
          <MetricCard
            label="Tokens / turn"
            value={
              tokens.tokens_per_turn === null || tokens.tokens_per_turn === undefined
                ? "—"
                : Number(tokens.tokens_per_turn).toFixed(1)
            }
            sub={`${fmtInt(tokens.measured_total)} tok total`}
            sub2={`${fmtInt(tokens.llm_calls)} llm calls declared`}
            note={tokens.instrumented ? null : tokens.note ?? "tokens are not instrumented for this run"}
          />
          <MetricCard
            label="% zero-LLM"
            value={fmtPct(necessity.pct_zero_llm, 0)}
            sub={`${fmtInt(necessity.zero_llm_turns)} of ${fmtInt(
              necessity.zero_llm_denominator
            )} metered turns made no call`}
            sub2={`${fmtInt(necessity.judged_turns)} of ${fmtInt(data?.total)} turns judged`}
            tone={
              necessity.pct_zero_llm === null || necessity.pct_zero_llm === undefined
                ? "default"
                : Number(necessity.pct_zero_llm) >= 0.5
                  ? "good"
                  : "default"
            }
            note={necessityNote}
            hint="Counted from the per-turn LLM CALL count, not from tokens: a turn is zero-LLM only when something metered it and it made no call. Unmetered turns are not in the denominator."
          />
          <MetricCard
            label="Avoidable tokens"
            value={
              necessity.avoidable_tokens === null || necessity.avoidable_tokens === undefined
                ? "—"
                : fmtInt(necessity.avoidable_tokens)
            }
            sub={`${fmtPct(necessity.avoidable_share, 1)} of ${fmtInt(necessity.judged_tokens)} judged tokens`}
            sub2={`${fmtInt(necessity.avoidable_calls)} avoidable calls${
              num(necessity.unattributed_calls) ? ` · ${fmtInt(necessity.unattributed_calls)} unattributed` : ""
            }`}
            tone={
              necessity.avoidable_share === null || necessity.avoidable_share === undefined
                ? "default"
                : Number(necessity.avoidable_share) >= 0.2
                  ? "bad"
                  : Number(necessity.avoidable_share) >= 0.05
                    ? "warn"
                    : "good"
            }
            note={necessityNote}
            hint="Tokens a deterministic judge says did not need a model: routing, translation, summarisation, and calls a cache had already answered. Calls with no per-call record are excluded from the denominator, not assumed necessary."
          />
          <MetricCard
            label="Persona"
            value={fmtInt(persona.distinct)}
            sub="distinct personas in this run"
            note={persona.instrumented ? null : persona.note ?? "persona is not instrumented for this run"}
          />
          <MetricCard
            label="Cache"
            value={fmtPct(cache.hit_rate, 0)}
            sub={`${fmtInt(cache.hits)} hits`}
            sub2={`over ${fmtInt(cache.observed_turns)} of ${fmtInt(data?.total)} turns that recorded a flag`}
            note={cache.instrumented ? null : cache.note ?? "no turn in this run recorded a cache flag"}
          />
          <MetricCard
            label="Latency p50"
            value={fmtMs(latency.p50_ms)}
            sub={`p90 ${fmtMs(latency.p90_ms)} · p95 ${fmtMs(latency.p95_ms)}`}
            sub2={`${fmtInt(latency.n)} timed turns`}
            note={latency.instrumented ? null : "no turn in this run recorded a latency"}
          />
          <MetricCard
            label="Latency tail"
            value={fmtMs(latency.p99_ms)}
            sub={`p99 · max ${fmtMs(latency.max_ms)}`}
            tone={num(latency.p99_ms) > 15000 ? "warn" : "default"}
            note={latency.instrumented ? null : "no turn in this run recorded a latency"}
          />
        </div>
      </section>

      <div className="row" style={{ alignItems: "flex-end" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Tabs tabs={tabs} active={tab} onChange={setTab} />
        </div>
        <Toggle checked={autoRefresh} onChange={setAutoRefresh} label="auto-refresh (30s)" />
      </div>

      {detail.loading && !data ? (
        <SkeletonRows n={6} cols={4} />
      ) : (
        <>
          {tab === "matrix" && (
            <MatrixTab
              detail={data}
              runId={runId}
              rowAxis={rowAxis}
              rowAxis2={rowAxis2}
              colAxis={colAxis}
              family={family}
              filters={filters}
              onRowAxis={setRowAxis}
              onRowAxis2={setRowAxis2}
              onColAxis={setColAxis}
              onFamily={setFamily}
              onFilter={setFilter}
              onClearFilters={() => setFilters({})}
            />
          )}
          {tab === "calibration" && <CalibrationTab detail={data} runId={runId} />}
          {tab === "journey" && <JourneyTab runId={runId} />}
          {tab === "analysis" && <AnalysisTab detail={data} runId={runId} canWrite={canWrite} />}
          {tab === "trend" && (
            <TrendTab
              runs={allRuns}
              runsLoading={runs.loading}
              runsError={runs.error}
              runId={runId}
              onSelectRun={selectRun}
            />
          )}
          {tab === "threads" && <ThreadsTab runId={runId} filters={filters} />}
          {tab === "lanes" && <LanesTab detail={data} />}
          {tab === "cache" && <CacheTab detail={data} />}
          {tab === "review" && <ReviewTab detail={data} runId={runId} canWrite={canWrite} />}
          {tab === "failures" && <FailuresTab detail={data} />}
        </>
      )}
    </div>
  );
}
